import React, { useEffect, useState, useCallback, useRef } from "react";
import InputIterator from "../../common/inputIterator";
import PrimarySideBar from "../../common/sideBar/primarySideBar";
import AddButtonSection from "../../common/AddButtonSection";
import { DevTool } from "@hookform/devtools";
import { useForm } from "react-hook-form";
import { patientFormRender as formRender } from "../../../utils/serviceData";
import CapturePhoto from "../../common/capturePhoto/capturePhoto";
import moment from "moment";
import Util, {
  generateValidObject,
  setVal,
  dateFormat,
} from "../../../utils/utils";
import { commonPostApi } from "../../../utils/apiService";
import { API_ENDPOINTS } from "../../../utils/apiEndpoints";
import Loader from "../../common/loader";
import { showToast } from "../../common/toast";
import { TOAST_TYPE, COMMON_MSG, FEATURE_MSG } from "../../../constants/toast";

export default function AddOrEditPatient({
  mode = "edit", // 'add' or 'edit'
  ...props
}) {
  const {
    control,
    formState: { errors, dirtyFields },
    handleSubmit,
    setValue,
    reset,
    watch,
  } = useForm({ mode: "onChange", reValidateMode: "onChange" });
  const [visible, setVisible] = useState(false);
  const [loading, setLoader] = useState(false);
  const [serverLoading, setServerLoading] = useState(false);
  const [uploadedImg, setUploadedImg] = useState(null);
  const fileInputRef = useRef(null);

  const MAX_FILE_SIZE = 800 * 1024; // 800KB in bytes
  const ALLOWED_TYPES = ['image/jpeg', 'image/gif', 'image/png'];

  // Determine if this is add or edit mode
  const isAddMode = mode === "add" || (!props?.id);

  const onImageCapture = (param) => {
    const capturedFile = Util.dataURLtoFile(
      param,
      `image-capture${moment().format("YYYYMMDDHHmmssa")}.jpg`
    );
    if (capturedFile) {
      if (!ALLOWED_TYPES.includes(capturedFile.type)) {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.INVALID_FILE_TYPE);
        return;
      }
      if (capturedFile.size > MAX_FILE_SIZE) {
        showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.FILE_SIZE_EXCEEDED);
        return;
      }
      setUploadedImg(capturedFile);
      setValue("profile_picture", capturedFile.name);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const onSubmit = async (data) => {
    if (serverLoading) return;
    setServerLoading(true);

    // File size validation
    if (uploadedImg && uploadedImg.size && uploadedImg.size > MAX_FILE_SIZE) {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.FILE_SIZE_EXCEEDED);
      setServerLoading(false);
      return;
    }

    let validObj = generateValidObject(data);
    const dob = dateFormat(validObj["dob"]);
    validObj = {
      ...validObj,
      dob: dob,
    };

    // Helper function to prepare FormData and headers
    const prepareRequestData = (payloadObj) => {
      let finalData;
      let headers;

      if (uploadedImg && uploadedImg.size && uploadedImg.size > 0) {
        finalData = new FormData();
        finalData.append("profile_picture", uploadedImg);
        finalData.append("_payload", JSON.stringify(payloadObj));
        headers = {
          "Content-Type": "multipart/form-data",
        };
      } else {
        finalData = payloadObj;
        headers = {
          "Content-Type": "application/json",
        };
      }

      return { finalData, headers };
    };

    // Helper function to handle success
    const handleSuccess = (successMessage) => {
      showToast(TOAST_TYPE.SUCCESS, successMessage);
      props?.setVisible?.(false, true);
      props?.initCallBack?.();
      reset();
      setUploadedImg(null);
      setServerLoading(false);
    };

    // Helper function to handle error
    const handleError = () => {
      showToast(TOAST_TYPE.ERROR, COMMON_MSG.ERROR.GENERIC);
      setServerLoading(false);
    };

    // For add mode: send all fields, always submit
    // For edit mode: only submit if there are changes
    if (isAddMode) {
      // Add mode: send all fields
      const { finalData, headers } = prepareRequestData(validObj);

      await commonPostApi(API_ENDPOINTS.ADD_PATIENT_DETAILS, finalData, { headers })
        .then((data) => handleSuccess(FEATURE_MSG.PATIENT.ADDED))
        .catch((err) => handleError());
    } else {
      // Edit mode: only send changed fields
      if (
        Object.entries(dirtyFields).length > 0 ||
        (uploadedImg && uploadedImg?.size > 0)
      ) {
        const newObj = {};

        // Only include changed fields
        Object.keys(dirtyFields).forEach((key) => {
          formRender.forEach((item) => {
            if (item.name === key) {
              newObj[item.fieldLabel] = validObj[key];
            } else if (item?.item) {
              item?.item?.forEach((subItem) => {
                if (subItem.name === key) {
                  newObj[subItem.fieldLabel] = validObj[key];
                }
              });
            }
          });
        });

        if (props?.id) newObj["patient_id"] = props?.id;

        const { finalData, headers } = prepareRequestData(newObj);

        await commonPostApi(API_ENDPOINTS.EDIT_PATIENT_DETAILS, finalData, { headers })
          .then((data) => handleSuccess(FEATURE_MSG.PATIENT.UPDATED))
          .catch((err) => handleError());
      } else {
        setServerLoading(false);
      }
    }
  };

  const dataUpdate = useCallback((patient) => {
    setVal(formRender, patient?.originalData, (key, value) => {
      setValue(key, value);
    });
    setValue("profile_picture", patient?.originalData?.profile_picture);
  }, [setValue]);

  const fetchPatientDetails = useCallback(async () => {
    setLoader(true);
    await commonPostApi(API_ENDPOINTS.SEARCH_PATIENT_DETAILS, {
      patient_id: props?.id,
    }).then((data) => {
      const patientDetails = data?.data?.patient;
      patientDetails.gender = patientDetails.gender.toLowerCase();
      dataUpdate({ originalData: patientDetails });
      setLoader(false);
    });
  }, [props?.id, dataUpdate]);

  const buttons = [
    {
      spType: "file",
      type: "primaryButton",
      label: isAddMode ? "Upload Profile Picture" : "Edit Profile Picture",
      class: "col-9",
      icon: isAddMode ? "pi pi-upload" : "pi pi-pencil",
      info: uploadedImg?.name,
      inputRef: fileInputRef,
      watch: watch && watch,
      watchState: "profile_picture",
      onClick: (e) => {
        const file = e?.target?.files?.[0];
        e.target.value = "";
        if (file) {
          if (!ALLOWED_TYPES.includes(file.type)) {
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.INVALID_FILE_TYPE);
            e.target.value = '';
            return;
          }
          if (file.size > MAX_FILE_SIZE) {
            showToast(TOAST_TYPE.ERROR, COMMON_MSG.VALIDATION.FILE_SIZE_EXCEEDED);
            e.target.value = '';
            return;
          }
          setValue("profile_picture", file.name);
          setUploadedImg(file);
        }
      },
    },
    {
      type: "secondaryButton",
      label: "Reset",
      class: "col-3",
      onClick: () => {
        setUploadedImg(null);
        setValue("profile_picture", "");

        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    },
    {
      type: "render",
      render: (
        <div className="msgText font-normal text-sm p-2 pb-3">
          Allowed JPG, GIF or PNG. Max size of 800KB
        </div>
      ),
    },
    {
      type: "render",
      render: (
        <CapturePhoto
          imgWidth={200}
          imgHeight={150}
          webcamWidth={410}
          webcamHeight={320}
          onImageCapture={onImageCapture}
          showToast={showToast}
        />
      ),
    },
    {
      type: "primarySuccessButton",
      label: isAddMode ? "Submit" : "Save Changes",
      disabled: isAddMode ? false : (Object.keys(dirtyFields).length === 0 && !uploadedImg),
      class: isAddMode ? "col-4" : "col-6",
      onClick: handleSubmit(onSubmit),
    },
    {
      type: isAddMode ? "primaryCancelButton" : "secondaryButton",
      label: isAddMode ? "Cancel" : "Discard",
      class: isAddMode ? "col-4" : "col-4",
      onClick: () => {
        reset();
        setUploadedImg(null);
        props?.setVisible?.(false);
      },
    },
  ];

  useEffect(() => {
    setVisible(props?.visible);
  }, [props?.visible]);

  useEffect(() => {
    // Only load data for edit mode when id is provided
    if (!isAddMode && props?.id) {
      fetchPatientDetails();
    }
  }, [isAddMode, props?.id, fetchPatientDetails]);

  return (
    <React.Fragment>
      <PrimarySideBar
        visible={visible}
        onHide={() => {
          reset();
          setUploadedImg(null);
          props?.setVisible?.(false);
        }}
        header={<div className="sideBar-header">{isAddMode ? "Add Patient" : "Edit Patient"}</div>}
      >
        {loading ? (
          <Loader />
        ) : (
          <React.Fragment>
            <InputIterator
              control={control}
              errors={errors}
              formRender={formRender}
            />
            <div className="pt-3">
              <AddButtonSection disabled={serverLoading} buttons={buttons} />
            </div>
          </React.Fragment>
        )}
      </PrimarySideBar>
      <DevTool control={control} />
    </React.Fragment>
  );
}